/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ticketService } from "@src/@api-services/ticket";
import { TICKET_PRIORITY, TICKET_STATUS } from "../constants";

// ** Types
export interface Ticket {
  id: string | number;
  userType: string;
  linkedTo: string | null;
  category: string;
  title: string | null;
  assignedTo: number | null;
  notes: string | null;
  description: string;
  status: typeof TICKET_STATUS[keyof typeof TICKET_STATUS];
  priority: typeof TICKET_PRIORITY[keyof typeof TICKET_PRIORITY];
  isEscalated: boolean | number;
  dateSubmitted: string;
  lastUpdated: string;
  createdAt: string;
  updatedAt: string;
  images?: any[];
  threads?: any[];
  logs?: any[];
  [key: string]: any;
}

interface GetTicketsParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  orderBy?: string;
  orderDirection?: 'ASC' | 'DESC';
  [key: string]: any;
}

interface TicketsState {
  data: Ticket[];
  total: number;
  params: GetTicketsParams;
  selectedTicket: Ticket | null;
  loading: boolean;
  error: string | null;
}

// ** Async Thunks
export const getTickets = createAsyncThunk(
  "tickets/getTickets",
  async (params: GetTicketsParams, { rejectWithValue }) => {
    try {
      const response = await ticketService.getTickets(params);
      return {
        params,
        data: response.data.data.data,
        total: response.data.data.totalRecord,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch tickets"
      );
    }
  }
);

export const getTicket = createAsyncThunk(
  "tickets/getTicket",
  async (id: string | number, { rejectWithValue }) => {
    try {
      const response = await ticketService.getTicket(id);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch ticket"
      );
    }
  }
);

export const updateTicketStatus = createAsyncThunk(
  "tickets/updateStatus",
  async (
    { id, status }: { id: string | number; status: string },
    { dispatch, getState, rejectWithValue }: any
  ) => {
    try {
      const response = await ticketService.updateTicketStatus(id, { status });
      const state = getState();
      await dispatch(getTickets(state.tickets.params));
      await dispatch(getTicket(id)); // Refresh selected ticket to get new logs
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update ticket status"
      );
    }
  }
);

export const updateTicket = createAsyncThunk(
  "tickets/updateTicket",
  async (
    { id, data }: { id: string | number; data: any },
    { dispatch, getState, rejectWithValue }: any
  ) => {
    try {
      const response = await ticketService.updateTicket(id, data);
      const state = getState();
      await dispatch(getTickets(state.tickets.params));
      await dispatch(getTicket(id)); // Refresh selected ticket
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update ticket details"
      );
    }
  }
);

export const addTicketThread = createAsyncThunk(
  "tickets/addThread",
  async (
    { id, message }: { id: string | number; message: string },
    { dispatch, rejectWithValue }: any
  ) => {
    try {
      const response = await ticketService.addTicketThread(id, { message });
      await dispatch(getTicket(id)); // Refresh selected ticket to get new thread
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add ticket message"
      );
    }
  }
);

export const escalateTicket = createAsyncThunk(
  "tickets/escalate",
  async (
    { id }: { id: string | number },
    { dispatch, rejectWithValue }: any
  ) => {
    try {
      const response = await ticketService.escalateTicket(id);
      await dispatch(getTicket(id)); // Refresh to get updated priority & isEscalated
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to escalate ticket"
      );
    }
  }
);

const initialState: TicketsState = {
  data: [],
  total: 0,
  params: {
    page: 1,
    pageSize: 10,
    orderBy: 'id',
    orderDirection: 'ASC'
  },
  selectedTicket: null,
  loading: false,
  error: null,
};

export const ticketsSlice = createSlice({
  name: "tickets",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedTicket: (state) => {
      state.selectedTicket = null;
    },
    setParams: (state, action) => {
      state.params = { ...state.params, ...action.payload };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTickets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTickets.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.params = action.payload.params;
        state.total = action.payload.total;
      })
      .addCase(getTickets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.data = [];
        state.total = 0;
      })
      .addCase(getTicket.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTicket.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedTicket = action.payload;
      })
      .addCase(getTicket.rejected, (state) => {
        state.loading = false;
        state.selectedTicket = null;
      })
      .addCase(updateTicketStatus.fulfilled, (state, action) => {
        const { id, status } = action.meta.arg;
        
        // Update selectedTicket if it's the one being modified
        if (state.selectedTicket && String(state.selectedTicket.id) === String(id)) {
          state.selectedTicket.status = status as any;
        }

        // Update in the data list
        const index = state.data.findIndex(r => String(r.id) === String(id));
        if (index !== -1) {
          state.data[index].status = status as any;
        }
      });
  },
});

export const { clearError, clearSelectedTicket, setParams } = ticketsSlice.actions;

export default ticketsSlice.reducer;
